import React, { useState, useEffect } from 'react';
import {
    Select, Button, Row, Col, Card, Input, Dropdown, App, Spin, Divider, Table, Modal, Menu, Collapse, Space, Badge, Tag, Typography, Tooltip
} from 'antd';
import {
    EllipsisOutlined, UserAddOutlined, SaveOutlined, PlayCircleOutlined,
    MailOutlined, DeleteOutlined, GiftOutlined, TeamOutlined, TrophyOutlined,
    CheckCircleOutlined, CloseCircleOutlined, EditOutlined, InfoCircleOutlined,
    ShareAltOutlined, WhatsAppOutlined, CopyOutlined, LinkOutlined, ReloadOutlined
} from '@ant-design/icons';
import { useParams, useNavigate, Link } from 'react-router-dom';
import MenuLayout from "../Layouts/MenuLayout";
import useAxiosInstance from "../Hooks/axiosInstance";

const { Option } = Select;
const { Title, Paragraph, Text } = Typography;

interface Exclusion {
    player: string;
    cant_receive_from: string[];
}

interface Data {
    titolo: string;
    players: string[];
    exclusions: Exclusion[];
    couples: Couple[];
}

interface Couple {
    receiver: string;
    gifter: string;
}

// Helper function to extract error message from backend
const getErrorMessage = (error: any): string => {
    if (error?.response?.data?.msg) {
        return error.response.data.msg;
    }
    if (error?.response?.data?.error?.message) {
        return error.response.data.error.message;
    }
    if (error?.message) {
        return error.message;
    }
    return String(error);
};

const Sorteggio: React.FC = () => {
    const [data, setData] = useState<Data>({ titolo: "", players: [], exclusions: [], couples: [] });
    const [newPlayer, setNewPlayer] = useState<string>('');
    const [isEmailValid, setIsEmailValid] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [editingPlayer, setEditingPlayer] = useState<string | null>(null);
    const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 768);
    const [collapseExpanded, setCollapseExpanded] = useState<string[]>([]);
    const [visibleReceivers, setVisibleReceivers] = useState<Set<string>>(new Set());
    const { message, modal } = App.useApp();
    const { id } = useParams<{ id: string }>();
    const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
    const navigate = useNavigate();
    const axiosInstance = useAxiosInstance();

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (!id) {
            // Reset data when creating a new game
            setData({ titolo: "", players: [], exclusions: [], couples: [] });
            setLoading(false);
            return;
        }
        const fetchGameData = async () => {
            setLoading(true);
            try {
                const response = await axiosInstance.get<Data>(`${apiEndpoint}/games/${id}`);
                const gameData = response.data || { titolo: "", players: [], exclusions: [], couples: [] };
                // Ensure all arrays are initialized
                setData({
                    titolo: gameData.titolo || "",
                    players: gameData.players || [],
                    exclusions: gameData.exclusions || [],
                    couples: gameData.couples || []
                });
            } catch (error) {
                modal.error({ title: "Attenzione", content: "Errore nel recupero dei dati dall'API: " + getErrorMessage(error) });
            } finally {
                setLoading(false);
            }
        };
        fetchGameData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, apiEndpoint]);


    const handleSave = async () => {
        console.log(axiosInstance)
        if (!data.titolo.trim()) {
            modal.error({ title: "Attenzione", content: "Il titolo è obbligatorio" });
            return;
        }
        const endpoint = id ? `${apiEndpoint}/games/${id}` : `${apiEndpoint}/games`;
        try {
            const response = await axiosInstance.post(endpoint, { data });
            const savedData = response.data.data || { titolo: "", players: [], exclusions: [], couples: [] };
            // Ensure all arrays are initialized
            setData({
                titolo: savedData.titolo || "",
                players: savedData.players || [],
                exclusions: savedData.exclusions || [],
                couples: savedData.couples || []
            });
            if (!id && savedData.uuid) navigate(`/sorteggio/${savedData.uuid}`);
            message.success("Dati salvati correttamente!");
        } catch (error) {
            modal.error({ title: "Attenzione", content: "Errore durante il salvataggio dei dati: " + getErrorMessage(error) });
        }
    };

    const handleDelete = async () => {
        Modal.confirm({
            title: 'Conferma eliminazione',
            content: 'Sei sicuro di voler eliminare il sorteggio? Nessuno potrà mai recuperarlo. Mai.' ,
            okText: 'Elimina',
            cancelText: 'Annulla',
            okButtonProps: { danger: true },
            onOk: async () => {
                try {
                    await axiosInstance.delete(`${apiEndpoint}/games/${id}`);
                    message.success("Sorteggio eliminato correttamente!");
                    navigate('/sorteggi');
                } catch (error) {
                    modal.error({ title: "Attenzione", content: "Errore durante l'eliminazione del sorteggio: " + getErrorMessage(error) });
                }
            },
            onCancel() {
                message.info('Eliminazione annullata');
            },
        });
    };

    const playGame = async () => {
        // Check if there are at least 2 players
        if (!data.players || data.players.length < 2) {
            Modal.error({
                title: 'Impossibile sorteggiare',
                icon: <GiftOutlined style={{ color: '#d32f2f' }} />,
                content: (
                    <div>
                        <Paragraph style={{ marginBottom: '12px' }}>
                            <strong>Servono almeno 2 giocatori per fare un sorteggio!</strong>
                        </Paragraph>
                        <Paragraph style={{ fontSize: '14px', color: '#666', marginBottom: 0 }}>
                            Aggiungi altri partecipanti prima di avviare il sorteggio. 🎅
                        </Paragraph>
                    </div>
                )
            });
            return;
        }

        Modal.confirm({
            title: 'Conferma sorteggio',
            icon: <GiftOutlined style={{ color: '#d32f2f' }} />,
            content: 'Sei sicuro di voler avviare/riavviare il sorteggio?',
            okText: 'Sorteggia!',
            cancelText: 'Annulla',
            onOk: async () => {
                try {
                    // Salva automaticamente i dati prima di sorteggiare
                    if (!data.titolo.trim()) {
                        modal.error({ title: "Attenzione", content: "Il titolo è obbligatorio" });
                        return;
                    }

                    message.loading({ content: 'Salvataggio in corso...', key: 'sorteggio', duration: 0 });

                    const endpoint = id ? `${apiEndpoint}/games/${id}` : `${apiEndpoint}/games`;
                    const saveResponse = await axiosInstance.post(endpoint, { data });
                    const savedData = saveResponse.data.data || { titolo: "", players: [], exclusions: [], couples: [] };
                    setData(savedData);

                    if (!id && savedData.uuid) {
                        navigate(`/sorteggio/${savedData.uuid}`);
                        message.destroy('sorteggio');
                        return;
                    }

                    message.loading({ content: 'Sorteggio in corso...', key: 'sorteggio', duration: 0 });

                    const response = await axiosInstance.get(`${apiEndpoint}/games/${id}/play`);
                    message.destroy('sorteggio');

                    if (response.data?.status) {
                        setData(prevData => ({
                            ...prevData,
                            couples: response.data.couples || []
                        }));
                        message.success({
                            content: 'Sorteggio completato con successo! 🎁',
                            duration: 3
                        });
                    } else {
                        Modal.error({ title: "Attenzione", content: response.data?.msg || "Errore sconosciuto" });
                    }
                } catch (error: any) {
                    message.destroy('sorteggio');
                    const errorMsg = getErrorMessage(error);

                    // Check if it's the "impossible to complete couples" error
                    if (error?.response?.status === 500 && errorMsg.toLowerCase().includes('completare le')) {
                        Modal.error({
                            title: "Impossibile completare il sorteggio",
                            content: (
                                <div>
                                    <p><strong>⚠️ Non è possibile creare le coppie con le esclusioni attuali.</strong></p>
                                    <p style={{ marginTop: '12px', fontSize: '14px', color: '#666' }}>
                                        Le esclusioni che hai impostato rendono impossibile assegnare un regalo a tutti i partecipanti.
                                        Prova a:
                                    </p>
                                    <ul style={{ marginTop: '8px', paddingLeft: '20px', fontSize: '14px', color: '#666' }}>
                                        <li>Ridurre il numero di esclusioni</li>
                                        <li>Aggiungere più partecipanti</li>
                                        <li>Rivedere le esclusioni reciproche</li>
                                    </ul>
                                </div>
                            ),
                            width: 500
                        });
                    } else {
                        Modal.error({
                            title: "Errore durante il sorteggio",
                            content: errorMsg
                        });
                    }
                }
            },
            onCancel() {
                message.info('Sorteggio annullato');
            },
        });
    };

    const sendEmails = async () => {
        Modal.confirm({
            title: 'Conferma invio email',
            icon: <MailOutlined style={{ color: '#0288d1' }} />,
            content: 'Sei sicuro di voler inviare le email ai giocatori?',
            okText: 'Invia',
            cancelText: 'Annulla',
            onOk: async () => {
                try {
                    const response = await axiosInstance.post(`${apiEndpoint}/games/${id}/emails`);
                    if (response.data?.status) {
                        message.success({
                            content: 'Email inviate ai giocatori! 📧',
                            duration: 3
                        });
                    } else {
                        Modal.error({ title: "Attenzione", content: response.data?.msg || "Errore sconosciuto" });
                    }
                } catch (error) {
                    Modal.error({ title: "Attenzione", content: "Errore durante l'invio delle email: " + getErrorMessage(error) });
                }
            },
            onCancel() {
                message.info('Invio email annullato');
            },
        });
    };

    const handleAddPlayer = async () => {
        const emailRegex = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/;
        if (!newPlayer.trim() || !emailRegex.test(newPlayer)) {
            message.error("Inserisci un'email valida");
            return;
        }
        if (data.players.includes(newPlayer)) {
            message.error("Giocatore già inserito");
        } else {
            const newData = {
                ...data,
                players: [...data.players, newPlayer],
                exclusions: [...data.exclusions, { player: newPlayer, cant_receive_from: [] }]
            };
            setData(newData);
            setNewPlayer('');
            setIsEmailValid(false);
            message.success('Giocatore aggiunto!');

            // Auto-save after adding player
            if (id && data.titolo.trim()) {
                try {
                    await axiosInstance.post(`${apiEndpoint}/games/${id}`, { data: newData });
                } catch (error) {
                    // Silent save, don't show error to user
                    console.error('Auto-save error:', error);
                }
            }
        }
    };

    const handleBlurNewPlayer = async () => {
        if (newPlayer.trim() && isEmailValid && !data.players.includes(newPlayer)) {
            await handleAddPlayer();
        }
    };

    const handleNewPlayerChange = (value: string) => {
        setNewPlayer(value);
        const emailRegex = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/;
        setIsEmailValid(value.trim() !== '' && emailRegex.test(value));
    };

    const handleExcludeChange = (player: string, excluded: string[]) => {
        setData(prevData => ({
            ...prevData,
            exclusions: prevData.exclusions.map(exclusion =>
                exclusion.player === player ? { ...exclusion, cant_receive_from: excluded } : exclusion
            )
        }));
    };

    const handlePlayerAction = (action: string, player: string) => {
        if (action === "Elimina") {
            setData(prevData => ({
                ...prevData,
                players: prevData.players.filter(p => p !== player),
                exclusions: prevData.exclusions.filter(exclusion => exclusion.player !== player),
                couples: prevData.couples.filter(couple => couple.gifter !== player && couple.receiver !== player)
            }));
            message.success('Giocatore eliminato');
        } else if (action === "Rinomina") {
            setEditingPlayer(player);
        }
    };

    const handleRenamePlayer = (oldName: string, newName: string) => {
        if (!newName.trim()) {
            message.error("Il nome non può essere vuoto");
            return;
        }
        setData(prevData => ({
            ...prevData,
            players: prevData.players.map(p => p === oldName ? newName : p),
            exclusions: prevData.exclusions.map(exclusion =>
                exclusion.player === oldName
                    ? { ...exclusion, player: newName }
                    : {
                        ...exclusion,
                        cant_receive_from: exclusion.cant_receive_from.map(p => p === oldName ? newName : p)
                    }
            ),
            couples: prevData.couples.map(couple =>
                ({
                    ...couple,
                    gifter: couple.gifter === oldName ? newName : couple.gifter,
                    receiver: couple.receiver === oldName ? newName : couple.receiver
                })
            )
        }));
        setEditingPlayer(null);
        message.success('Giocatore rinominato');
    };

    const handleCopyLink = () => {
        if (!id) {
            message.warning('Salva prima il sorteggio per generare il link!');
            return;
        }
        const inviteLink = `${window.location.origin}/join/${id}`;
        navigator.clipboard.writeText(inviteLink).then(() => {
            message.success('Link copiato negli appunti!');
        }).catch(() => {
            message.error('Errore durante la copia del link');
        });
    };

    const handleShareWhatsApp = () => {
        if (!id) {
            message.warning('Salva prima il sorteggio per generare il link!');
            return;
        }
        const inviteLink = `${window.location.origin}/join/${id}`;
        const text = encodeURIComponent(
            `🎅 Ciao! Sei stato invitato a partecipare al Babbo Natale Segreto!\n\nAggiungi il tuo nome cliccando qui: ${inviteLink}`
        );
        window.open(`https://wa.me/?text=${text}`, '_blank');
    };

    const handleResetDraw = async () => {
        Modal.confirm({
            title: '🔄 Resetta il Sorteggio',
            icon: <GiftOutlined style={{ color: '#f57c00' }} />,
            content: (
                <div>
                    <Paragraph style={{ marginBottom: '12px' }}>
                        <strong>Stai per cancellare tutte le coppie sorteggiate!</strong>
                    </Paragraph>
                    <Paragraph style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
                        Cancellando le coppie potrai:
                    </Paragraph>
                    <ul style={{ paddingLeft: '20px', fontSize: '14px', color: '#666', marginBottom: '12px' }}>
                        <li>Aggiungere o rimuovere partecipanti</li>
                        <li>Modificare le esclusioni</li>
                        <li>Rifare il sorteggio da capo</li>
                    </ul>
                    <Paragraph style={{ fontSize: '14px', color: '#d32f2f', marginBottom: 0 }}>
                        <strong>⚠️ Attenzione!</strong> Se hai già inviato le email ai partecipanti,
                        resetare il sorteggio li confonderà parecchio! Assicurati di avvisarli prima! 😅
                    </Paragraph>
                </div>
            ),
            okText: 'Sì, resetta tutto',
            cancelText: 'Meglio di no',
            okButtonProps: { danger: true },
            width: 520,
            onOk: async () => {
                try {
                    message.loading({ content: 'Reset in corso...', key: 'reset', duration: 0 });
                    const response = await axiosInstance.post(`${apiEndpoint}/games/${id}/reset-draw`);
                    message.destroy('reset');

                    // Update the game data with the response, ensuring proper structure
                    const resetData = response.data.data || response.data || { titolo: "", players: [], exclusions: [], couples: [] };
                    setData({
                        titolo: resetData.titolo || "",
                        players: resetData.players || [],
                        exclusions: resetData.exclusions || [],
                        couples: resetData.couples || []
                    });

                    message.success({
                        content: 'Sorteggio resettato! Puoi ora modificare i partecipanti 🎉',
                        duration: 3
                    });
                } catch (error) {
                    message.destroy('reset');
                    Modal.error({
                        title: "Errore durante il reset",
                        content: getErrorMessage(error)
                    });
                }
            },
            onCancel() {
                message.info('Reset annullato');
            },
        });
    };

    const handleCollapseChange = (keys: string | string[]) => {
        const keysArray = Array.isArray(keys) ? keys : [keys];
        const isExpanding = keysArray.length > collapseExpanded.length;

        if (isExpanding && keysArray.includes('1')) {
            Modal.confirm({
                title: '🤫 Attenzione: Spoiler Alert!',
                icon: <GiftOutlined style={{ color: '#f57c00' }} />,
                content: (
                    <div>
                        <Paragraph style={{ marginBottom: '12px' }}>
                            <strong>Stai per svelare il segreto del Babbo Natale! 🎅</strong>
                        </Paragraph>
                        <Paragraph style={{ fontSize: '14px', color: '#666' }}>
                            Se partecipi al gioco, <strong>NON dovresti vedere</strong> questa schermata!
                            Controlla solo la tua email per scoprire a chi dovrai fare il regalo.
                        </Paragraph>
                        <Paragraph style={{ fontSize: '14px', color: '#666' }}>
                            Se stai aiutando un giocatore che ha perso l'email vai avanti e copia il link segreto da inviare. Vai avanti .. 👀
                        </Paragraph>
                        <Paragraph style={{ fontSize: '14px', color: '#d32f2f', marginBottom: 0 }}>
                            ⚠️ Ma ricorda: guardando le coppie rovinerai comunque la sorpresa per te stesso!
                        </Paragraph>
                    </div>
                ),
                okText: 'Ho capito, mostrami comunque',
                cancelText: 'Meglio di no!',
                okButtonProps: { danger: true },
                width: 520,
                onOk: () => {
                    setCollapseExpanded(keysArray);
                    setVisibleReceivers(new Set());
                },
                onCancel: () => {
                    message.info('Bene! Il segreto resta tale 🤐');
                }
            });
        } else {
            setCollapseExpanded(keysArray);
            if (keysArray.length === 0) {
                setVisibleReceivers(new Set());
            }
        }
    };

    const handleReloadData = async () => {
        if (!id) {
            message.warning('Salva prima il sorteggio per ricaricare i dati');
            return;
        }

        setLoading(true);
        try {
            const response = await axiosInstance.get<Data>(`${apiEndpoint}/games/${id}`);
            const gameData = response.data || { titolo: "", players: [], exclusions: [], couples: [] };
            setData({
                titolo: gameData.titolo || "",
                players: gameData.players || [],
                exclusions: gameData.exclusions || [],
                couples: gameData.couples || []
            });
            message.success('Dati ricaricati! 🔄');
        } catch (error) {
            modal.error({ title: "Attenzione", content: "Errore durante il ricaricamento: " + getErrorMessage(error) });
        } finally {
            setLoading(false);
        }
    };

    const toggleReceiverVisibility = (gifter: string) => {
        setVisibleReceivers(prev => {
            const newSet = new Set(prev);
            const isCurrentlyVisible = newSet.has(gifter);

            if (isCurrentlyVisible) {
                // Just hide it
                newSet.delete(gifter);
                return newSet;
            } else {
                // Show with confirmation
                Modal.confirm({
                    title: '🔓 Stai per rivelare il segreto!',
                    icon: <GiftOutlined style={{ color: '#f57c00' }} />,
                    content: (
                        <div>
                            <Paragraph style={{ marginBottom: '12px' }}>
                                Sei sicuro di voler vedere a chi <strong>{gifter}</strong> deve fare il regalo?
                            </Paragraph>
                            <Paragraph style={{ fontSize: '13px', color: '#d32f2f', marginBottom: 0 }}>
                                ⚠️ Se <strong>{gifter}</strong> sei tu, rovinerai la sorpresa! Meglio controllare solo la tua email! 📧
                            </Paragraph>
                        </div>
                    ),
                    okText: 'Sì, mostrami',
                    cancelText: 'No, torna indietro',
                    okButtonProps: { danger: true },
                    width: 480,
                    onOk: () => {
                        newSet.add(gifter);
                        setVisibleReceivers(new Set(newSet));
                    }
                });

                // Don't update state yet, wait for confirmation
                return prev;
            }
        });
    };

    return (
        <MenuLayout pageTitle={isMobile ? "Secret Santa" : "Secret Santa - Sorteggio"}>
            <div className="sorteggio-container" style={{
                padding: isMobile ? '12px' : '24px',
                background: '#ffffff',
                borderRadius: '16px',
                minHeight: '100vh'
            }}>
                {/* Header Intro */}
                <Card
                    style={{
                        marginBottom: '24px',
                        background: 'linear-gradient(135deg, #d32f2f 0%, #c62828 100%)',
                        border: 'none',
                        borderRadius: '16px'
                    }}
                    bodyStyle={{ padding: isMobile ? '16px' : '24px' }}
                >
                    <div style={{ textAlign: 'center', color: 'white' }}>
                        <GiftOutlined style={{ fontSize: isMobile ? '36px' : '48px', marginBottom: '12px' }} />
                        <Title level={isMobile ? 4 : 3} style={{ color: 'white', marginBottom: '8px' }}>
                            Benvenuto nel Secret Santa!
                        </Title>
                        <Paragraph
                            ellipsis={{ rows: 2, expandable: true, symbol: 'Leggi tutto' }}
                            style={{ color: 'rgba(255,255,255,0.95)', fontSize: isMobile ? '13px' : '15px', marginBottom: 0 }}
                        >
                            Crea il tuo sorteggio personalizzato per lo scambio di regali. Aggiungi i partecipanti, imposta le esclusioni
                            e lascia che la magia faccia il resto! 🎅
                        </Paragraph>
                    </div>
                </Card>

                <Spin spinning={loading} size="large">
                    {/* Title Input */}
                    <Card
                        style={{ marginBottom: '24px', borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                        bodyStyle={{ padding: isMobile ? '16px' : '20px' }}
                    >
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            <Text strong style={{ fontSize: '15px', color: '#666' }}>
                                Titolo del sorteggio
                            </Text>
                            <Input
                                placeholder="es. Natale in Famiglia 2024"
                                value={data.titolo}
                                onChange={(e) => setData({ ...data, titolo: e.target.value })}
                                size="large"
                                prefix={<TrophyOutlined style={{ color: '#d32f2f' }} />}
                            />
                        </Space>
                    </Card>

                    {/* Players Section */}
                    <Card
                        style={{ marginBottom: '24px', borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                        bodyStyle={{ padding: isMobile ? '16px' : '20px' }}
                    >
                        <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                            <Space>
                                <TeamOutlined style={{ fontSize: '24px', color: '#d32f2f' }} />
                                <Title level={isMobile ? 5 : 4} style={{ margin: 0 }}>
                                    Partecipanti
                                </Title>
                                <Badge
                                    count={data.players?.length || 0}
                                    style={{ backgroundColor: '#2e7d32' }}
                                    showZero
                                />
                                {id && (
                                    <Tooltip title="Ricarica per vedere se si sono aggiunti nuovi giocatori tramite link">
                                        <Button
                                            type="text"
                                            icon={<ReloadOutlined />}
                                            onClick={handleReloadData}
                                            size="small"
                                            style={{ color: '#1890ff' }}
                                        > AGGIORNA GIOCATORI </Button>
                                    </Tooltip>
                                )}
                            </Space>
                            <Tooltip
                                title={
                                    <div style={{ fontSize: '13px', lineHeight: '1.5' }}>
                                        <strong>💡 Cosa sono le Esclusioni?</strong><br/>
                                        Seleziona chi NON può fare un regalo a questo giocatore.<br/>
                                        Utile per coppie, familiari o chi ha già scambiato regali in passato!
                                    </div>
                                }
                                color="#d32f2f"
                            >
                                <InfoCircleOutlined style={{ fontSize: '18px', color: '#666', cursor: 'help' }} />
                            </Tooltip>
                        </div>

                        {!id && (
                            <div style={{
                                background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
                                padding: isMobile ? '12px 16px' : '16px 20px',
                                borderRadius: '12px',
                                border: '2px solid #f57c00',
                                marginBottom: '20px'
                            }}>
                                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                    <Text strong style={{ color: '#e65100', fontSize: '14px' }}>
                                        🎯 Come iniziare
                                    </Text>
                                    <Paragraph style={{ margin: 0, fontSize: '13px', color: '#bf360c', lineHeight: '1.6' }}>
                                        <strong>1.</strong> Inserisci il titolo sopra<br/>
                                        <strong>2.</strong> Clicca "Crea Sorteggio"<br/>
                                        <strong>3.</strong> Aggiungi i partecipanti tu o condividi il link 🎅
                                    </Paragraph>
                                </Space>
                            </div>
                        )}

                        <Row gutter={isMobile ? [12, 12] : [16, 16]}>
                            {(data.exclusions || []).map(({ player, cant_receive_from }) => (
                                <Col key={player} xs={24} sm={12} md={8} lg={6}>
                                    <Card
                                        className="player-card-festive"
                                        title={
                                            editingPlayer === player ? (
                                                <Input
                                                    defaultValue={player}
                                                    onPressEnter={(e) => handleRenamePlayer(player, e.currentTarget.value)}
                                                    onBlur={(e) => handleRenamePlayer(player, e.currentTarget.value)}
                                                    autoFocus
                                                    size="small"
                                                />
                                            ) : (
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px'
                                                }}>
                                                    <CheckCircleOutlined style={{ color: '#2e7d32' }} />
                                                    <span style={{
                                                        fontSize: isMobile ? '14px' : '15px',
                                                        fontWeight: 600,
                                                        wordBreak: 'break-word',
                                                        flex: 1
                                                    }}>
                                                        {player}
                                                    </span>
                                                </div>
                                            )
                                        }
                                        extra={
                                            (data.couples?.length || 0) === 0 && (
                                                <Dropdown
                                                    overlay={
                                                        <Menu>
                                                            <Menu.Item
                                                                key="rinomina"
                                                                icon={<EditOutlined />}
                                                                onClick={() => handlePlayerAction("Rinomina", player)}
                                                            >
                                                                Rinomina
                                                            </Menu.Item>
                                                            <Menu.Item
                                                                key="elimina"
                                                                danger
                                                                icon={<DeleteOutlined />}
                                                                onClick={() => handlePlayerAction("Elimina", player)}
                                                            >
                                                                Elimina
                                                            </Menu.Item>
                                                        </Menu>
                                                    }
                                                    trigger={["click"]}
                                                >
                                                    <Button type="text" icon={<EllipsisOutlined />} size="small" />
                                                </Dropdown>
                                            )
                                        }
                                        size="small"
                                        style={{
                                            borderRadius: '12px',
                                            boxShadow: '0 2px 8px rgba(211,47,47,0.12)',
                                            border: '1px solid #ffe0e0',
                                            height: '100%',
                                            transition: 'all 0.3s ease'
                                        }}
                                    >
                                        <Select
                                            mode="multiple"
                                            placeholder="🚫 Esclusioni (non può ricevere da ...)"
                                            value={cant_receive_from}
                                            onChange={(excluded) => handleExcludeChange(player, excluded)}
                                            style={{ width: '100%' }}
                                            size={isMobile ? 'middle' : 'middle'}
                                            suffixIcon={<CloseCircleOutlined />}
                                            disabled={(data.couples?.length || 0) > 0}
                                        >
                                            {(data.players || []).filter(p => p !== player).map(p => (
                                                <Option key={p}>{p}</Option>
                                            ))}
                                        </Select>
                                        {cant_receive_from.length > 0 && (
                                            <div style={{ marginTop: '8px' }}>
                                                {cant_receive_from.map(excluded => (
                                                    <Tag
                                                        key={excluded}
                                                        color="error"
                                                        style={{ marginTop: '4px', fontSize: '11px' }}
                                                    >
                                                        {excluded}
                                                    </Tag>
                                                ))}
                                            </div>
                                        )}
                                    </Card>
                                </Col>
                            ))}

                            {/* Add New Player Card */}
                            {id && (data.couples?.length || 0) === 0 && (
                                <Col xs={24} sm={12} md={8} lg={6}>
                                    <Card
                                        className="add-player-card-festive"
                                        style={{
                                            borderRadius: '12px',
                                            border: '2px dashed #d32f2f',
                                            background: 'linear-gradient(135deg, #fff 0%, #fffef9 100%)',
                                            boxShadow: '0 2px 8px rgba(211,47,47,0.08)',
                                            height: '100%',
                                            minHeight: '180px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            transition: 'all 0.3s ease'
                                        }}
                                        bodyStyle={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            flex: 1
                                        }}
                                    >
                                        <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                                            <UserAddOutlined style={{ fontSize: '32px', color: '#d32f2f' }} />
                                            <div style={{
                                                fontWeight: 600,
                                                color: '#d32f2f',
                                                marginTop: '8px',
                                                fontSize: '15px'
                                            }}>
                                                Nuovo Partecipante
                                            </div>
                                            <Text style={{
                                                fontSize: '12px',
                                                color: '#666',
                                                display: 'block',
                                                marginTop: '6px',
                                                lineHeight: '1.4'
                                            }}>
                                                📧 Email obbligatoria, nome opzionale
                                            </Text>
                                            <Text style={{
                                                fontSize: '11px',
                                                color: '#999',
                                                display: 'block',
                                                marginTop: '4px'
                                            }}>
                                                💡 mario@email.com o Mario Rossi mario@email.com
                                            </Text>
                                        </div>
                                        <Input
                                            placeholder="mario@email.com o Mario Rossi mario@email.com"
                                            value={newPlayer}
                                            onChange={e => handleNewPlayerChange(e.target.value)}
                                            onPressEnter={handleAddPlayer}
                                            onBlur={handleBlurNewPlayer}
                                            style={{ marginBottom: '10px' }}
                                            size={isMobile ? 'middle' : 'middle'}
                                            autoComplete="off"
                                            suffix={
                                                isEmailValid && (
                                                    <CheckCircleOutlined
                                                        style={{
                                                            color: '#2e7d32',
                                                            fontSize: '18px',
                                                            animation: 'fadeIn 0.3s ease-in'
                                                        }}
                                                    />
                                                )
                                            }
                                        />
                                        <Button
                                            type="primary"
                                            onClick={handleAddPlayer}
                                            icon={<UserAddOutlined />}
                                            block
                                            size={isMobile ? 'middle' : 'middle'}
                                        >
                                            Aggiungi
                                        </Button>
                                    </Card>
                                </Col>
                            )}
                        </Row>

                        {/* Save Button */}
                        {(data.couples?.length || 0) === 0 && (
                            <div style={{ marginTop: '20px', textAlign: 'center' }}>
                                <Button
                                    type="primary"
                                    icon={<SaveOutlined />}
                                    onClick={handleSave}
                                    size="large"
                                    style={{ minWidth: '160px' }}
                                >
                                    {!id ? 'Crea Sorteggio' : 'Salva Modifiche'}
                                </Button>
                            </div>
                        )}

                        <Divider style={{ margin: '24px 0' }} />

                        {/* Share Link Section */}
                        {id && (data.couples?.length || 0) === 0 && (
                            <Card
                                style={{
                                    marginBottom: '16px',
                                    background: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)',
                                    border: '2px solid #ba68c8',
                                    borderRadius: '12px'
                                }}
                                bodyStyle={{ padding: isMobile ? '12px' : '16px' }}
                            >
                                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                        <LinkOutlined style={{ fontSize: '20px', color: '#7b1fa2' }} />
                                        <Text strong style={{ color: '#7b1fa2', fontSize: '15px' }}>
                                            Invita i tuoi amici!
                                        </Text>
                                    </div>
                                    <Paragraph style={{ margin: 0, fontSize: '13px', color: '#6a1b9a' }}>
                                        Condividi questo link per permettere ad altri di aggiungersi al gioco automaticamente
                                    </Paragraph>
                                    <Space
                                        direction={isMobile ? 'vertical' : 'horizontal'}
                                        size="small"
                                        style={{ width: isMobile ? '100%' : 'auto', marginTop: '8px' }}
                                    >
                                        <Button
                                            icon={<WhatsAppOutlined />}
                                            onClick={handleShareWhatsApp}
                                            size={isMobile ? 'middle' : 'large'}
                                            block={isMobile}
                                            style={{
                                                background: '#25D366',
                                                borderColor: '#25D366',
                                                color: 'white',
                                                minWidth: isMobile ? '100%' : '180px'
                                            }}
                                        >
                                            Condividi su WhatsApp
                                        </Button>
                                        <Button
                                            icon={<CopyOutlined />}
                                            onClick={handleCopyLink}
                                            size={isMobile ? 'middle' : 'large'}
                                            block={isMobile}
                                            style={{
                                                minWidth: isMobile ? '100%' : '150px'
                                            }}
                                        >
                                            Copia Link
                                        </Button>
                                    </Space>
                                </Space>
                            </Card>
                        )}
                    </Card>

                    {/* Results Section */}
                    {(data.couples?.length || 0) > 0 && (
                        <Card
                            style={{
                                marginBottom: '24px',
                                borderRadius: '16px',
                                boxShadow: '0 4px 16px rgba(46,125,50,0.12)',
                                border: '2px solid #e8f5e9'
                            }}
                            bodyStyle={{ padding: isMobile ? '16px' : '20px' }}
                        >
                            <div style={{ marginBottom: '16px', textAlign: 'center' }}>
                                <GiftOutlined style={{ fontSize: '32px', color: '#2e7d32', marginBottom: '8px' }} />
                                <Title level={isMobile ? 5 : 4} style={{ margin: 0, color: '#2e7d32' }}>
                                    Risultati Sorteggio
                                </Title>
                                <Text type="secondary" style={{ fontSize: '13px', display: 'block', marginTop: '8px' }}>
                                    Apri questa sezione per copiare i link segreti da inviare a chi ha perso l'email
                                </Text>
                            </div>
                            <Collapse
                                bordered={false}
                                style={{ background: 'transparent' }}
                                expandIconPosition="end"
                                activeKey={collapseExpanded}
                                onChange={handleCollapseChange}
                            >
                                <Collapse.Panel
                                    header={
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            fontSize: isMobile ? '14px' : '15px',
                                            fontWeight: 600
                                        }}>
                                            <TrophyOutlined style={{ color: '#f57c00' }} />
                                            {data.couples?.length || 0} coppie sorteggiate
                                            <Text type="secondary" style={{ fontSize: '13px', fontWeight: 400 }}>
                                                (clicca per visualizzare)
                                            </Text>
                                        </div>
                                    }
                                    key="1"
                                    style={{
                                        background: '#fff',
                                        borderRadius: '12px',
                                        border: '1px solid #e0e0e0',
                                        marginBottom: '8px'
                                    }}
                                >
                                    <div style={{ overflowX: 'auto' }}>
                                        <Table
                                            dataSource={data.couples || []}
                                            columns={[
                                                {
                                                    title: <><GiftOutlined /> Chi regala</>,
                                                    dataIndex: 'gifter',
                                                    key: 'gifter',
                                                    ellipsis: true,
                                                    render: (text) => <Tag color="volcano">{text}</Tag>
                                                },
                                                {
                                                    title: <><GiftOutlined /> Chi Riceve</>,
                                                    dataIndex: 'receiver',
                                                    key: 'receiver',
                                                    ellipsis: true,
                                                    render: (text, record) => {
                                                        const isVisible = visibleReceivers.has(record.gifter);
                                                        const encodedReceiver = btoa(unescape(encodeURIComponent(text)));
                                                        const revealLink = `${window.location.origin}/reveal?text=${encodedReceiver}`;

                                                        return (
                                                            <Space>
                                                                {isVisible ? (
                                                                    <Tag color="green">{text}</Tag>
                                                                ) : (
                                                                    <Tag color="default" style={{ color: '#999', userSelect: 'none' }}>
                                                                        ••••••••
                                                                    </Tag>
                                                                )}
                                                                <Button
                                                                    type="link"
                                                                    size="small"
                                                                    onClick={() => toggleReceiverVisibility(record.gifter)}
                                                                    style={{ padding: '0 4px' }}
                                                                >
                                                                    {isVisible ? '👁️ Nascondi' : '👁️ Mostra'}
                                                                </Button>
                                                                <Tooltip title="Copia link segreto da inviare al giocatore">
                                                                    <Button
                                                                        size="small"
                                                                        icon={<LinkOutlined />}
                                                                        onClick={() => {
                                                                            navigator.clipboard.writeText(revealLink).then(() => {
                                                                                message.success(`Link segreto copiato per ${record.gifter}! Mandaglielo via WhatsApp o come preferisci.`);
                                                                            }).catch(() => {
                                                                                message.error('Errore durante la copia del link');
                                                                            });
                                                                        }}
                                                                        style={{ padding: '0 4px', color: '#7b1fa2' }}
                                                                    >
                                                                        Link segreto
                                                                    </Button>
                                                                </Tooltip>
                                                            </Space>
                                                        );
                                                    }
                                                }
                                            ]}
                                            pagination={false}
                                            rowKey={(record) => `${record.gifter}-${record.receiver}`}
                                            size="small"
                                            scroll={isMobile ? { x: 'max-content' } : undefined}
                                        />
                                    </div>
                                </Collapse.Panel>
                            </Collapse>
                        </Card>
                    )}

                    {/* Main Action Buttons */}
                    {id && (
                        <Card
                            style={{ marginBottom: '24px', borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                            bodyStyle={{ padding: isMobile ? '16px' : '20px' }}
                        >
                            <Space
                                direction={isMobile ? 'vertical' : 'horizontal'}
                                size="middle"
                                style={{ width: '100%', justifyContent: 'center' }}
                            >
                                <Button
                                    type="primary"
                                    icon={<PlayCircleOutlined />}
                                    onClick={playGame}
                                    size="large"
                                    block={isMobile}
                                    style={{
                                        minWidth: isMobile ? '100%' : '200px',
                                        background: (data.couples?.length || 0) ? '#f57c00' : '#d32f2f',
                                        borderColor: (data.couples?.length || 0) ? '#f57c00' : '#d32f2f'
                                    }}
                                >
                                    {(data.couples?.length || 0) ? '🔄 Risorteggia' : '🎲 Sorteggia Coppie'}
                                </Button>
                                {(data.couples?.length || 0) > 0 && (
                                    <Button
                                        type="default"
                                        icon={<MailOutlined />}
                                        onClick={sendEmails}
                                        size="large"
                                        block={isMobile}
                                        style={{
                                            minWidth: isMobile ? '100%' : '200px',
                                            borderColor: '#0288d1',
                                            color: '#0288d1'
                                        }}
                                    >
                                        📧 Invia Email
                                    </Button>
                                )}
                            </Space>
                        </Card>
                    )}
                </Spin>

                {/* Delete and Reset Buttons */}
                {id && (
                    <div style={{ textAlign: 'center', marginTop: '32px' }}>
                        <Space direction={isMobile ? 'vertical' : 'horizontal'} size="middle">
                            {(data.couples?.length || 0) > 0 && (
                                <Button
                                    type="text"
                                    icon={<PlayCircleOutlined style={{ transform: 'rotate(180deg)' }} />}
                                    onClick={handleResetDraw}
                                    style={{ fontSize: isMobile ? '14px' : '15px', color: '#f57c00' }}
                                >
                                    Resetta sorteggio
                                </Button>
                            )}
                            <Button
                                danger
                                type="text"
                                icon={<DeleteOutlined />}
                                onClick={handleDelete}
                                style={{ fontSize: isMobile ? '14px' : '15px' }}
                            >
                                Elimina sorteggio
                            </Button>
                        </Space>
                    </div>
                )}
            </div>
        </MenuLayout>
    );
};

export default Sorteggio;
